
















































import { defineComponent, onUnmounted, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { Vue } from "vue/types/vue";
import useKeepCustomScrollPosition from "@/hooks/useKeepCustomScrollPosition";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSendMessage from "@/components/BaseSendMessage";
import CustomScroll from "@/components/CustomScroll";
import VolumeInput from "@/components/GlobalAudioPlayer/components/VolumeInput.vue";
import { LiveMessage } from "@/types/olecams";
import usePWPlayerCommunicator from "../../hooks/usePWPlayerCommunicator";

export default defineComponent({
  name: "StreamChat",
  components: {
    BaseIconButton,
    BaseSendMessage,
    CustomScroll,
    VolumeInput,
  },
  props: {
    sessionToken: { type: String, required: true },
    consumeUser: { type: Object as () => { id: string; username: string }, required: true },
    showPrivateButtons: { type: Boolean, default: false },
    showChat: { type: Boolean, default: false },
    showVideoControls: { type: Boolean, default: false },
  },
  setup(props) {
    const messageListNode = ref<Vue | undefined>();
    const value = ref("");
    const scrollbarWidth = ref(0);
    const sending = ref(false);
    const messageList = ref<LiveMessage[]>([]);
    const volume = ref(50);
    const muted = ref(false);

    let chatCommunicator: null | ReturnType<typeof usePWPlayerCommunicator> = null;

    const { shouldScrollToBottom, scrollNode } = useKeepCustomScrollPosition(messageListNode, { behavior: "smooth" });

    const onNewMessageReceived = (message: LiveMessage) => {
      messageList.value = messageList.value.concat([message]);
      if (!scrollNode.value) return;
      const bottomScrollTopPosition = scrollNode.value.scrollHeight - scrollNode.value.offsetHeight;
      if (message.nick === props.consumeUser.username || bottomScrollTopPosition - scrollNode.value.scrollTop <= 100) {
        shouldScrollToBottom.value = true;
      }
    };

    const initCommunicator = () => {
      chatCommunicator = usePWPlayerCommunicator({
        sessionToken: props.sessionToken,
        onChatMessage: (data: LiveMessage) => {
          onNewMessageReceived(data);
        },
      });
    };

    // Send message function using chat communicator
    const sendMessage = (message: string, cb?: () => void) => {
      if (chatCommunicator) {
        chatCommunicator.sendMessage(message);
        if (cb) cb();
      }
    };

    // Volume control using chat communicator
    const toggleMuted = (newMuted: boolean) => {
      if (chatCommunicator) {
        muted.value = newMuted;
        chatCommunicator.setMuted(newMuted);
      }
    };
    const requestVIP = () => {
      if (chatCommunicator) {
        chatCommunicator.requestVIP();
      }
    };

    const goToGroupChat = () => {
      if (chatCommunicator) {
        chatCommunicator.goToGroupChat();
      }
    };

    watch(
      volume,
      debounce(newVolume => {
        if (chatCommunicator) {
          chatCommunicator.setVolume(newVolume / 100);
        }
      }, 150)
    );

    initCommunicator();

    onUnmounted(() => {
      chatCommunicator?.cleanup();
    });

    return {
      messageList,
      sending,
      messageListNode,
      scrollbarWidth,
      value,
      volume,
      muted,
      sendMessage,
      toggleMuted,
      requestVIP,
      goToGroupChat,
    };
  },
});
